/** What the app says to you while you are looking at it.
 *
 *  The first thing in this app that interrupts. Everything else waits to be read — a
 *  badge, a list, a banner on the screen you happened to open — which was fine while
 *  Telegram carried the interrupting. It does not always: a message is dropped when
 *  the day has not been published, when the person has no Telegram, or when Telegram
 *  refuses it, and all three are silent. So the app has to be able to say it itself.
 *
 *  **It polls; there is no live connection.** Every fifteen seconds while the tab is
 *  focused, and not at all while it is hidden — `refetchOnWindowFocus` catches up the
 *  moment somebody comes back, which is better than polling a tab nobody is looking
 *  at. No websocket, no SSE, nothing held open through Caddy by the single process
 *  that also runs every scheduled job.
 *
 *  Three rules make it usable rather than hated, and all three live in
 *  `Notices.pop.ts` where they can be tested: never replay the backlog, never pop the
 *  same notice twice, and coalesce a flurry into one.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { api } from '@/api/client'
import { useAuth } from '@/lib/auth'
import { severalLabel, toPop, type Notice, type Popped } from '@/pages/Notices.pop'

type Shown = { id: string; volume: 'sticky' | 'brief'; title: string
               detail: string; to: string }

/** How long a passing note stays. Long enough to read a sentence and glance at a ref,
 *  short enough not to sit over the thing somebody is typing into. */
const BRIEF_MS = 8000
/** Three at once, and the rest counted. A stack taller than this stops being a
 *  notification and becomes a wall. */
const MAX_STACK = 3

/** The high-water mark, per person, so a reload does not replay what has already been
 *  shown. Wrapped because `localStorage` throws outright in a private window and in any
 *  browser set to block site data - the same reason `lib/theme.ts` wraps it, and a
 *  notification must never be the thing that stops the app rendering. */
function loadSeen(userId: string): Set<string> {
  try {
    const raw = localStorage.getItem(`tm.seen.${userId}`)
    return new Set(raw ? (JSON.parse(raw) as string[]) : [])
  } catch { return new Set() }
}
function saveSeen(userId: string, seen: Set<string>) {
  try {
    // Bounded: only the most recent matter, and an unbounded list would grow for ever
    // in somebody's browser for no benefit.
    localStorage.setItem(`tm.seen.${userId}`,
                         JSON.stringify([...seen].slice(-200)))
  } catch { /* unstorable is not unusable - it just re-pops after a reload */ }
}

export function Toasts() {
  const { me } = useAuth()
  const [shown, setShown] = useState<Shown[]>([])
  const seen = useRef<Set<string>>(new Set())
  const primed = useRef(false)

  const { data } = useQuery({
    queryKey: ['notices-recent'],
    queryFn: () => api.get<{ items: Notice[] }>('/notices?unread=true&page_size=20'),
    enabled: !!me,
    refetchInterval: 15_000,
    // Not while the tab is hidden. `refetchOnWindowFocus` is already on globally, so
    // coming back catches up at once - polling a tab nobody is looking at only costs
    // the server.
    refetchIntervalInBackground: false,
  })

  useEffect(() => {
    if (!me || !data) return
    if (!primed.current) {
      // The first answer of the session is the BACKLOG, not news. Recording it as seen
      // is what stops somebody back from leave being buried in forty toasts; the rail
      // badge is how they find out it is there.
      primed.current = true
      seen.current = loadSeen(me.user_id)
      data.items.forEach((n) => seen.current.add(n.notice_id))
      saveSeen(me.user_id, seen.current)
      return
    }
    const popped: Popped[] = toPop(data.items, seen.current)
    if (!popped.length) return
    data.items.forEach((n) => seen.current.add(n.notice_id))
    saveSeen(me.user_id, seen.current)
    setShown((was) => [...was, ...popped.map(asShown)].slice(-MAX_STACK))
  }, [data, me])

  useEffect(() => {
    const brief = shown.filter((t) => t.volume === 'brief')
    if (!brief.length) return
    const timers = brief.map((t) => window.setTimeout(
      () => setShown((was) => was.filter((x) => x.id !== t.id)), BRIEF_MS))
    return () => timers.forEach(window.clearTimeout)
  }, [shown])

  // Escape clears the lot, which is the one keyboard affordance a stack of these
  // needs - they are not focusable and must not be in the tab order.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShown([]) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const sticky = useMemo(() => shown.some((t) => t.volume === 'sticky'), [shown])
  if (!shown.length) return null

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 w-[min(92vw,340px)]"
         // Polite for a passing note; assertive only when somebody is blocked until
         // this person acts. A screen reader announcing every date change over what
         // its user is reading is the same mistake as popping one.
         role="status" aria-live={sticky ? 'assertive' : 'polite'}>
      {shown.map((t) => (
        <div key={t.id}
             className={`rounded-lg border shadow-lg px-3.5 py-2.5 bg-surface
                         motion-safe:animate-[fadeIn_.15s_ease-out] ${
               t.volume === 'sticky' ? 'border-primary' : 'border-rule'}`}>
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <Link to={t.to} onClick={() => dismiss(setShown, t.id)}
                    className="block text-[13px] font-semibold leading-snug
                               hover:text-primary">
                {t.title}
              </Link>
              {t.detail ? (
                <p className="text-[12px] text-ink-2 mt-0.5 line-clamp-2">{t.detail}</p>
              ) : null}
            </div>
            <button onClick={() => dismiss(setShown, t.id)}
                    aria-label="Dismiss"
                    className="text-ink-3 hover:text-ink leading-none px-1">×</button>
          </div>
        </div>
      ))}
    </div>
  )
}

const dismiss = (set: React.Dispatch<React.SetStateAction<Shown[]>>, id: string) =>
  set((was) => was.filter((x) => x.id !== id))

function asShown(p: Popped): Shown {
  if (p.kind === 'several') {
    return {
      id: p.notices.map((n) => n.notice_id).join('+'),
      volume: 'brief',
      title: severalLabel(p.notices),
      // Counted rather than listed: five summaries stacked inside one toast is the
      // wall of text the coalescing existed to avoid.
      detail: '',
      to: '/notices',
    }
  }
  return {
    id: p.notice.notice_id,
    volume: p.volume === 'sticky' ? 'sticky' : 'brief',
    title: p.notice.summary,
    detail: p.notice.detail ?? '',
    to: p.notice.link_path || '/notices',
  }
}
